import { fireTrackingPixel } from './tracking-pixel';

class BannerAd {
    mediaTailor = null;
    constructor(options) {

        // Validate options
        if (!options.container) {
            console.error('BannerAd: Missing container');
            return;
        } else if (!options.videoElement) {
            console.error('BannerAd: Missing videoElement');
            return;
        }

        this.screenWidthToDimensions = {
            1280: { width: 890, height: 110 },
            1920: { width: 1335, height: 165 },
            2560: { width: 1780, height: 220 },
            3840: { width: 2670, height: 330 }
        };

        // Set options
        this.container = options.container;
        this.container.style.display = 'none';
        this.videoElement = options.videoElement;
        this.vastTag = options.vastTag;
        this.nonce = options.nonce;
        this.banner = options.streamsApiResponse.ads.banner;
        this.player = options.player;
        this.mediaTailor = options.mediaTailor;
        this.timeout = 30;

        this.cachedImgSrc = null;
        this.cachedBaseImageUrl = null;
        this.cachedImpressionUrls = null;
        this.cachedClickthroughHref = null;
        this.cachedClickthroughTrackingUrls = null;

        this.resizeObserver = new ResizeObserver(() => {
            if (this.bannerElement) {
                // Re-calculate banner size when container resizes
                this.hideBanner();
                // Only re-show if we have valid banner data
                if (this.banner && this.vastTag) {
                    setTimeout(() => this.showBanner(), 100);
                }
            }
        });
        this.resizeObserver.observe(this.container);

        // this.init();
        this.detectCuePoints();

        // Set up banner hiding on user interactions
        this.setupBannerHideOnInteraction(this.player);

        // Enable this to test - the banner will always stay one
        // this.tempConsoleLogs().then(() => {
        // this.showBanner();
        // });

        this.isInAdBreak = false;
        if (this.mediaTailor) {
            if (this.mediaTailor) {
                this.mediaTailor.session.addEventListener(
                    this.mediaTailor.mediatailor.SessionUiEvent.AD_START,
                    this.handleAdStart.bind(this)
                );
            }

            this.mediaTailor.session.addEventListener(
                this.mediaTailor.mediatailor.SessionUiEvent.AD_END,
                this.handleAdEnd.bind(this)
            );
        }
        this.log('vast', this.vastTag);
    }

    log(text, target) {
        return console.log('...' + text, target);
    }

    // Enabled for testing. To be removed once approved
    async tempConsoleLogs() {
        console.log('...banner ', this.banner);
        
    }

    // Fire multiple impression trackers
    async fireImpressionTrackers(urls) {
        if (!urls || !Array.isArray(urls)) return;
        
        // Fire all trackers in parallel and wait for all to complete
        await Promise.allSettled(
            urls.map(url => {
                if (url) {
                    return fireTrackingPixel(url)
                        .catch(error => console.error('...Error firing tracking pixel:', error, url));
                }
                return Promise.resolve();
            })
        );
    }

    // Fire multiple click trackers (similar to impressions)
    async fireClickTrackers(urls) {
        if (!urls || !Array.isArray(urls)) return;

        // Fire all trackers in parallel and wait for all to complete
        await Promise.allSettled(
            urls.map(url => {
                if (url) {
                    return fireTrackingPixel(url)
                        .catch(error => console.error('...Error firing click tracking pixel:', error, url));
                }
                return Promise.resolve();
            })
        );
    }

    // MARK: Detect cue points
    detectCuePoints() {
        const cuepointsArray = this.banner.cuepoints
            .split(',')
            .map(Number)
            .sort((a, b) => a - b);

        let lastCuepoint = -1;

        this.videoElement.addEventListener('timeupdate', () => {
            if (this.isInAdBreak) {
                return;
            }

            const t = this.videoElement.currentTime;

            // Find the closest cuepoint
            let closestCuepoint = -1;
            let minDistance = Infinity;

            for (const cuepoint of cuepointsArray) {
                const distance = Math.abs(t - cuepoint);
                if (distance < minDistance) {
                    minDistance = distance;
                    closestCuepoint = cuepoint;
                }
            }

            if (minDistance < 0.5 && closestCuepoint !== lastCuepoint) {
                lastCuepoint = closestCuepoint;
                this.showBanner();
            }

            if (minDistance >= 0.5 && lastCuepoint !== -1) {
                lastCuepoint = -1;
            }
        });
    }


    // MARK: Show banner
    async showBanner() {
        if(this.player.subtitles.list().some(subtitle => subtitle.enabled)) {
            return;
        }

        // Remove existing banner if it exists
        if (this.bannerElement) {
            this.bannerElement.remove();
            this.bannerElement = null;
        }

        clearTimeout(this.bannerHideTimer);

        // Check if parentElement exists before showing the container
        if (!this.container.parentElement) {
            console.error("Parent element is null, cannot show banner");
            return;
        }

        this.container.style.display = 'block';
        this.container.classList.add('visible');

        const base = this.screenWidthToDimensions[1920];
        const scale = this.container.parentElement.clientWidth / 1920;
        const bannerW = Math.round(base.width * scale);
        const bannerH = Math.round(base.height * scale);

        this.container.style.width = bannerW + 'px';
        this.container.style.height = bannerH + 'px';

        // Fetch VAST data if not cached
        let baseImageUrl;
        let impressionUrls;
        let clickthroughHref;
        let clickthroughTrackingUrls;
        if (this.cachedBaseImageUrl && this.cachedImpressionUrls && this.cachedClickthroughHref && this.cachedClickthroughTrackingUrls) {
            baseImageUrl = this.cachedBaseImageUrl;
            impressionUrls = this.cachedImpressionUrls;
            clickthroughHref = this.cachedClickthroughHref;
            clickthroughTrackingUrls = this.cachedClickthroughTrackingUrls;
            console.log('...Using cached base image URL, impressions, and clickthrough data');
        } else {
            try {
                const result = await getImageFromVast(
                    this.vastTag,
                    { width: bannerW, height: bannerH }
                );
                baseImageUrl = result.baseImageUrl;
                impressionUrls = result.impressionUrls;
                clickthroughHref = result.clickthroughHref;
                clickthroughTrackingUrls = result.clickthroughTrackingUrls;

                // Cache them
                this.cachedBaseImageUrl = baseImageUrl;
                this.cachedImpressionUrls = impressionUrls;
                this.cachedClickthroughHref = clickthroughHref;
                this.cachedClickthroughTrackingUrls = clickthroughTrackingUrls;
            } catch (err) {
                console.error("...❌ Could not get VAST data:", err);
                this.container.style.display = 'none';
                this.container.classList.remove('visible');
                return;
            }
        }

        const updatedUrl = new URL(baseImageUrl);
        updatedUrl.searchParams.set('sz', `${bannerW}x${bannerH}`);
        updatedUrl.searchParams.set('correlator', Date.now().toString());
        const imgSrc = updatedUrl.toString();

        if (impressionUrls && impressionUrls.length > 0) {
            await this.fireImpressionTrackers(impressionUrls);
            console.log('...Impression trackers fired for this show');
        }

        const bannerEl = document.createElement('div');
        bannerEl.style.cssText = `
            width: 100%;
            height: 100%;
            z-index: 100;
        `;

        try {
            const img = new Image();

            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('timeout')), 10000);
                img.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                };
                img.onerror = (e) => {
                    clearTimeout(timeout);
                    console.error("Image load error:", e);
                    reject(new Error('Image load failed'));
                };
                img.src = imgSrc;
            });

            const imgRatio = img.naturalWidth / img.naturalHeight;
            const boxRatio = bannerW / bannerH;

            let bgSize;
            if (imgRatio > boxRatio) {
                // image is wider → scale to height 100%, let width overflow
                bgSize = `auto ${bannerH}px`;
            } else {
                // image is taller → scale to width 100%, let height overflow
                bgSize = `${bannerW}px auto`;
            }

            bannerEl.style.backgroundImage = `url(${imgSrc})`;
            bannerEl.style.backgroundSize = bgSize;
            bannerEl.style.backgroundRepeat = "no-repeat";
            bannerEl.style.backgroundPosition = "center";

            // Make clickable if we have a clickthroughHref
            if (clickthroughHref) {
                bannerEl.style.cursor = 'pointer';
                bannerEl.addEventListener('click', async () => {
                    console.log('...Click on banner - firing trackers');
                    try {
                        if (clickthroughTrackingUrls && clickthroughTrackingUrls.length > 0) {
                            await this.fireClickTrackers(clickthroughTrackingUrls);
                            console.log('...Click trackers fired');
                        }
                    } catch (err) {
                        console.error('...Error firing click trackers:', err);
                    }
                    window.open(clickthroughHref, '_blank');
                    this.hideBanner();
                });
            }
        } catch (err) {
            console.error("...❌ Could not load VAST image:", err);
            this.container.style.display = 'none';
            this.container.classList.remove('visible');
            return;
        }

        this.container.appendChild(bannerEl);
        this.bannerElement = bannerEl;
        this.bannerHideTimer = setTimeout(
            () => this.hideBanner(),
            this.timeout * 1000
        );
    }

    hideBanner() {
        if (this.bannerElement) {
            this.bannerElement.remove();
            this.bannerElement = null;
        }
        clearTimeout(this.bannerHideTimer);
        this.container.classList.remove('visible');

        this.container.style.display = 'none';
    }

    setupBannerHideOnInteraction(player) {
        const hideBannerOnInteraction = (event) => {
            if (this.bannerElement) {
                console.log(`...Hiding banner due to user interaction: ${event.type}`);
                this.hideBanner();
            }
        };

        // Video playback events
        this.videoElement.addEventListener('pause', hideBannerOnInteraction);

        // These ones are fired on video load so I need to find a way to implement these 🚨🚨🚨
        // this.videoElement.addEventListener('play', hideBannerOnInteraction);
        // this.videoElement.addEventListener('seeking', hideBannerOnInteraction);
        // this.videoElement.addEventListener('seeked', hideBannerOnInteraction);

        // User interaction events on video
        if (player && player.getMediaElement) {
            const video = player.getMediaElement();
            video.addEventListener('click', hideBannerOnInteraction);
        }

        // Keyboard interactions
        document.addEventListener('keydown', hideBannerOnInteraction);

        this.hideBannerOnInteraction = hideBannerOnInteraction;
    }

    handleAdStart() {
        console.log('Ad has started - hiding banner ads');
        this.isInAdBreak = true;
        this.hideBanner();
    }

    handleAdEnd() {
        console.log('Ad has ended - re-enabling banner ads for cuepoints');
        this.isInAdBreak = false;
    }
}

// MARK: Get image from VAST
async function getImageFromVast(vastUrl, dimensions) {
    const resp = await fetch(vastUrl);
    if (!resp.ok) {
        throw new Error(`HTTP ${resp.status} loading VAST`);
    }

    let xml = await resp.text();

    // 2. Strip BOM if present
    xml = xml.replace(/^\uFEFF/, "");

    const dom = new DOMParser().parseFromString(xml, "application/xml");

    if (dom.getElementsByTagName("parsererror").length > 0) {
        console.error("...VAST XML parse error:", dom);
        throw new Error("...Invalid VAST XML");
    }

    const staticResources = dom.getElementsByTagName("StaticResource");

    const nonLinearAds = dom.getElementsByTagName("NonLinear");

    let baseImageUrl = null;
    let impressionUrls = [];
    let clickthroughHref = null;
    let clickthroughTrackingUrls = [];

    // First, try to find StaticResource elements
    for (let i = 0; i < staticResources.length; i++) {
        const node = staticResources[i];
        const type = (node.getAttribute("creativeType") || "").toLowerCase().trim();
        const url = node.textContent ? node.textContent.trim() : "";

        if (url) {
            // Check if it's an image by creativeType OR by URL extension
            const isImageByType = type.startsWith("image/") ||
                type.includes("png") ||
                type.includes("jpg") ||
                type.includes("jpeg");

            const isImageByUrl = url.toLowerCase().includes('.png') ||
                url.toLowerCase().includes('.jpg') ||
                url.toLowerCase().includes('.jpeg') ||
                url.toLowerCase().includes('.gif') ||
                url.toLowerCase().includes('.webp') ||
                url.includes('imgad'); // Google's image ad URLs often contain 'imgad'

            console.log("...Image checks:", { isImageByType, isImageByUrl, type, url });

            if (isImageByType || isImageByUrl || !type) {
                baseImageUrl = url;
                console.log("...✅ Selected this StaticResource as base image");
                break;
            }
        }
    }

    // Find impression trackers
    const impressionNodes = dom.getElementsByTagName("Impression");
    for (let i = 0; i < impressionNodes.length; i++) {
        const node = impressionNodes[i];
        const url = node.textContent ? node.textContent.trim() : "";
        if (url) {
            impressionUrls.push(url);
        }
    }

    // Get clickthrough href (from <NonLinearClickThrough>)
    const clickthroughNodes = dom.getElementsByTagName("NonLinearClickThrough");
    for (let i = 0; i < clickthroughNodes.length; i++) {
        const node = clickthroughNodes[i];
        const url = node.textContent ? node.textContent.trim() : "";
        if (url) {
            clickthroughHref = url;
            console.log('...Extracted clickthroughHref:', url);
            break; // Assuming only one; remove break if you want to collect multiples
        }
    }

    // Get clickthrough tracking URLs (from <NonLinearTracking> or correct tag)
    const clickthroughTrackingNodes = dom.getElementsByTagName("NonLinearTracking");
    for (let i = 0; i < clickthroughTrackingNodes.length; i++) {
        const node = clickthroughTrackingNodes[i];
        const url = node.textContent ? node.textContent.trim() : "";
        if (url) {
            clickthroughTrackingUrls.push(url);
            console.log('...Extracted clickthroughTrackingUrl:', url);
        }
    }

    if (!baseImageUrl) {
        throw new Error("...No image StaticResource found in VAST");
    }

    console.log("...✅ Using base StaticResource image URL:", baseImageUrl);
    return { baseImageUrl, impressionUrls, clickthroughHref, clickthroughTrackingUrls };
}

export default BannerAd;
